import { Box, Button, IconButton, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ArrowLeftIcon, ArrowRightIcon } from "@mui/x-date-pickers";
import clsx from "clsx";
import * as React from "react";
import { FormEvent } from "react";
import LoginPanel from "../containers/LoginPanel";
import { Form, KeyValue } from "../interfaces";

const useStyles = makeStyles(theme => ({
  findBankButton: {
    "&.MuiButton-containedSecondary": {
      margin: "10px auto",
      display: "block",
      padding: "0px 24px",
    },
    "&.MuiButton-containedPrimary": {
      "width": 160,
      "fontSize": "1rem",
      "height": 40,
      "display": "block",
      "margin": "0 auto",
      "&:hover, &.Mui-focusVisible": {
        color: "#1A4480",
        backgroundColor: "#fff",
        border: "1px solid #ACC8DF",
      },
      "&:active": {
        backgroundColor: "#7B97BC",
        borderColor: "#1A4480",
        color: "#fff",
      },
    },
  },
  formStyles: {
    "paddingLeft": "1rem",
    "paddingRight": "1rem",
    "& ul": {
      padding: 0,
      margin: 0,
    },
    "& li": {
      "padding": "0",
      "display": "block",
      "& h2": {
        paddingBottom: 0,
      },

      "& h3, & h4": { marginBottom: "1rem", paddingBottom: 0 },
      "& .MuiFormControl-marginDense": {
        marginBottom: "1rem",
        margin: 0,
      },
    },
  },
  showPanelButton: {
    "borderRadius": 4,
    "backgroundColor": theme.palette.secondary.main,
    "color": "#1A4480",
    "border": "1px solid #acc8df",
    "transition": "none",
    "height": 40,
    "width": 40,
    "padding": 0,
    "&:hover, &:focus": {
      color: "#ffffff",
      backgroundColor: "#1A4480",
    },
    "&:active": {
      color: "#1A4480",
      backgroundColor: "#ffffff",
    },
  },
  hidePanelButton: {
    "marginTop": 0,
    "marginBottom": ".5rem",
    "width": "100%",
    "textTransform": "capitalize",
    "fontSize": 14,
    "fontWeight": 400,
    "color": "#1A4480",
    "borderBottomRightRadius": 0,
    "borderBottomLeftRadius": 0,
    "transition": "none",
    "borderBottom": "1px solid #acc8df",
    "&:hover, &:focus": {
      color: "#ffffff",
      backgroundColor: "#1A4480",
    },
    "&:active": {
      color: "#1A4480",
      backgroundColor: "#ffffff",
    },
  },
  searchButton: {
    "paddingTop": "1.5rem",
    "paddingBottom": "1.5rem",
    "& button.MuiButton-containedSecondary": {
      "margin": "10px auto",
      "display": "block",
      "padding": "0px 24px",
      "backgroundColor": "#1a4480",
      "color": "white",
      "&:hover, &.Mui-focusVisible": {
        color: "#1A4480",
        backgroundColor: "#fff",
        border: "1px solid #ACC8DF",
      },
    },
    "& button.MuiButton-containedPrimary": {
      "width": 160,
      "fontSize": "1rem",
      "height": 40,
      "display": "block",
      "margin": "0px auto",
      "&:hover, &.Mui-focusVisible": {
        color: "#1A4480",
        backgroundColor: "#fff",
        border: "1px solid #ACC8DF",
      },
      "&:active": {
        backgroundColor: "#7B97BC",
        borderColor: "#1A4480",
        color: "#fff",
      },
    },
  },
  loginButton: {
    "paddingTop": 0,
    "& button": { height: 40, padding: 7, fontSize: 16 },
  },
}));
export function SearchForm({
  formList,
  form,
  onSubmit,
  searchPanelExpand,
  setSearchPanelExpand,
  canHideSearchPanel,
  disabled,
  prevForm,
  setPrevForm,
  header,
  searchButtonText,
  secondaryButton,
  externalSubmit,
  noChangeSubmit,
  hideSearchButton,
  login,
  scroll,
  optionalIndicator,
  onReset,
  displayReset,
}: {
  formList: React.ReactElement;
  form: Form;
  onSubmit: (form: Form, submitted?: boolean) => void;
  searchPanelExpand: boolean;
  setSearchPanelExpand: (updatedSearchPanelExpand: boolean) => void;
  canHideSearchPanel: boolean;
  prevForm: KeyValue;
  setPrevForm: (prevForm: KeyValue) => void;
  header?: string;
  disabled?: boolean;
  externalSubmit?: boolean;
  searchButtonText?: string;
  secondaryButton?: boolean;
  noChangeSubmit?: boolean;
  hideSearchButton?: boolean;
  login?: boolean;
  scroll?: boolean;
  optionalIndicator?: boolean;
  onReset?: () => void;
  displayReset?: boolean;
}) {
  const classes = useStyles();

  function submitForm(e: FormEvent) {
    e.preventDefault();
    if (JSON.stringify(prevForm) !== JSON.stringify(form) || noChangeSubmit) {
      setPrevForm(form);
      onSubmit(form, noChangeSubmit);
    }
    if (scroll) {
      window.scrollTo(0, 150);
    }
  }

  React.useEffect(() => {
    if (externalSubmit) {
      if (JSON.stringify(prevForm) !== JSON.stringify(form)) {
        setPrevForm(form);
        onSubmit(form);
      }
    }
  });

  const handleCollapse = () => {
    setSearchPanelExpand(!searchPanelExpand);
  };

  const formContainer = (children: React.ReactElement) =>
    hideSearchButton ? (
      <div className={classes.formStyles}>{children}</div>
    ) : (
      <form
        id="search-panel-form"
        onSubmit={submitForm}
        className={classes.formStyles}
        noValidate
        autoComplete="off"
      >
        {children}

        <div
          className={clsx(classes.searchButton, login && classes.loginButton)}
          id="search-form-submit"
        >
          {!login ? (
            <>
              {optionalIndicator && (
                <Typography
                  color="#1a4480"
                  textAlign={"center"}
                  variant="body2"
                >
                  All fields are optional.
                </Typography>
              )}
              {displayReset ? (
                <Box display="flex" sx={{ gap: 2 }}>
                  <Button
                    id="resetSearch"
                    disableElevation
                    variant="contained"
                    disabled={disabled}
                    sx={{
                      backgroundColor: "#ACC8DF",
                      color: "#1A4480",
                      border: "1px solid #1A4480",
                    }}
                    onClick={() => {
                      onReset && onReset();
                    }}
                  >
                    Reset
                  </Button>

                  <Button
                    id="findBankSearch"
                    disableElevation
                    color={secondaryButton ? "secondary" : "primary"}
                    variant="contained"
                    type="submit"
                    disabled={disabled}
                    sx={{ padding: 0 }}
                  >
                    {searchButtonText || "Search"}
                  </Button>
                </Box>
              ) : (
                <Button
                  id="findBankSearch"
                  disableElevation
                  color={secondaryButton ? "secondary" : "primary"}
                  variant="contained"
                  type="submit"
                  disabled={disabled}
                >
                  {searchButtonText || "Search"}
                </Button>
              )}
            </>
          ) : (
            <LoginPanel formButton label="Continue via Login.gov" />
          )}
        </div>
      </form>
    );
  return (
    <>
      {searchPanelExpand ? (
        <>
          {header && (
            <Typography style={{ whiteSpace: "nowrap" }} variant="h2">
              {header}
            </Typography>
          )}
          <Box
            id="search-form-box"
            bgcolor="secondary.main"
            borderRadius="5px"
            justifyContent="center"
            border={"1px solid #ACC8DF"}
          >
            {canHideSearchPanel ? (
              <Button
                id="hide-search-panel-button"
                className={classes.hidePanelButton}
                onClick={handleCollapse}
                startIcon={<ArrowLeftIcon />}
              >
                Hide search panel
              </Button>
            ) : (
              undefined
            )}
            {formContainer(formList)}
          </Box>
        </>
      ) : (
        <IconButton
          id="show-search-panel-button"
          className={classes.showPanelButton}
          onClick={handleCollapse}
          size="large"
        >
          <ArrowRightIcon />
        </IconButton>
      )}
    </>
  );
}
